// src/features/faq/components/InactiveClientsFAQs.tsx

import React from 'react';
// ELIMINAMOS: import { useNavigate } from 'react-router-dom';
import { UserMinus, Search, Clock, MessageSquare, Calendar, Crown, ArrowRight } from 'lucide-react';

export interface InactiveClientsFAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface InactiveClientsFAQData {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  faqs: InactiveClientsFAQItem[];
}

// AÑADIMOS el parámetro 'navigateCallback'
export const InactiveClientsFAQs = (navigateCallback: (path: string) => void): InactiveClientsFAQData => {
  // ELIMINAMOS: const navigate = useNavigate();

  return {
    id: 'inactive-clients',
    title: 'Clientes Inactivos',
    icon: <UserMinus className="h-5 w-5 text-orange-600 dark:text-orange-400" />,
    description: 'Identificación y estrategias para recuperar clientes que no han visitado tu negocio recientemente.',
    faqs: [
      {
        id: 'inactive-access-pro', // Nueva pregunta prioritaria
        question: '¿Cómo accedo a la función de Clientes Inactivos?',
        answer: (
          <div className="bg-amber-50 dark:bg-amber-500/10 p-4 rounded-lg border border-amber-200 dark:border-amber-500/30">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 flex items-center mb-2">
              <Crown className="h-5 w-5 mr-2 text-yellow-600 dark:text-yellow-400" />
              Funcionalidad Exclusiva del Plan PRO
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              La identificación y gestión avanzada de Clientes Inactivos es una herramienta poderosa disponible exclusivamente para nuestros usuarios con <strong>suscripción PRO</strong>.
              Esta función te permite segmentar clientes por su período de inactividad, analizar patrones y ejecutar campañas de reactivación efectivas.
            </p>
            <div className="mt-3">
              <button 
                onClick={() => navigateCallback('/dashboard/subscription')} 
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Actualizar a PRO <ArrowRight className="ml-1.5 h-4 w-4" />
              </button>
            </div>
          </div>
        )
      },
      {
        id: 'inactive-purpose',
        question: '¿Para qué sirve la página de Clientes Inactivos?',
        answer: (
          <div>
            <p>La página de Clientes Inactivos (disponible en el plan PRO) es una herramienta estratégica que te permite:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Identificar clientes en riesgo:</strong> Detecta automáticamente aquellos clientes que no han visitado tu negocio durante un período de tiempo configurable.</li>
              <li><strong>Segmentar por inactividad:</strong> Filtra clientes según cuánto tiempo llevan sin visitarte (ej. 1 mes, 3 meses, 6 meses, 1 año, o una fecha específica).</li>
              <li><strong>Oportunidades de recuperación:</strong> Descubre qué clientes podrías estar perdiendo y necesitan un incentivo para regresar.</li>
              <li><strong>Base para campañas:</strong> Utiliza esta lista para crear <button onClick={() => navigateCallback('/dashboard/campaigns')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">campañas de reactivación</button> dirigidas y personalizadas.</li>
            </ul>
            <p className="mt-2 text-content-2">Entender y actuar sobre la inactividad de los clientes es clave para reducir la tasa de abandono (churn) y maximizar el valor de vida de cada cliente.</p>
            <div className="mt-2">
              <button onClick={() => navigateCallback('/dashboard/inactive-clients')} className="inline-flex items-center text-sm text-accent hover:text-blue-800 dark:text-blue-300">
                <UserMinus className="h-4 w-4 mr-1.5" />
                Ir a Clientes Inactivos
              </button>
            </div>
          </div>
        )
      },
      {
        id: 'inactive-definition',
        question: '¿Qué define a un cliente como "inactivo" en esta sección?',
        answer: (
          <div>
            <p>Un cliente se considera "inactivo" en esta sección si cumple con los criterios que tú estableces a través de los filtros. Principalmente, se basa en:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li><strong>El tiempo transcurrido</strong> desde su última visita registrada en el sistema.</li>
              <li><strong>El período de inactividad</strong> que seleccionas en los filtros de la página.</li>
            </ol>
            <p className="mt-2">La plataforma te ofrece varias opciones predefinidas para el período de inactividad:</p>
            <ul className="list-disc ml-5 mt-1 space-y-1">
              <li><strong>Último mes:</strong> Clientes sin visitas en los últimos 30 días.</li>
              <li><strong>Últimos 3 meses:</strong> Clientes sin visitas en los últimos 90 días.</li>
              <li><strong>Últimos 6 meses:</strong> Clientes sin visitas en los últimos 180 días.</li>
              <li><strong>Último año:</strong> Clientes sin visitas en los últimos 365 días.</li>
              <li><strong>Fecha específica:</strong> Te permite seleccionar una fecha concreta en el calendario, y el sistema listará todos los clientes cuya última visita fue anterior a esa fecha.</li>
            </ul>
            <div className="mt-2 bg-blue-50 dark:bg-blue-500/10 p-3 rounded-md border border-blue-200 dark:border-blue-500/30">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Consejo práctico:</strong> El período de inactividad "ideal" varía según el tipo de negocio. Para negocios con alta frecuencia de compra (ej. cafeterías), un mes de inactividad ya puede ser significativo. Para otros con ciclos más largos (ej. servicios anuales), podrías considerar períodos de 6 meses o más.
              </p>
            </div>
          </div>
        )
      },
      {
        id: 'inactive-filters',
        question: '¿Cómo puedo filtrar la lista de clientes inactivos?',
        answer: (
          <div>
            <p>La página de Clientes Inactivos (plan PRO) te proporciona herramientas flexibles para segmentar tu lista:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-3">
              <li>
                <strong>Selector de Período de Inactividad:</strong> En la parte superior, encontrarás un desplegable ("Inactivo desde") donde puedes elegir opciones predefinidas (1 mes, 3 meses, etc.) o la opción de "Fecha específica".
              </li>
              <li>
                <strong>Selector de Fecha Específica:</strong> Si eliges "Fecha específica", se activará un calendario (<Calendar className="inline h-4 w-4 text-content-2"/>) para que selecciones el día exacto a partir del cual considerar a un cliente inactivo.
              </li>
              <li>
                <strong>Búsqueda por Nombre o Teléfono:</strong> También cuentas con una barra de búsqueda (<Search className="inline h-4 w-4 text-content-2"/>) para encontrar un cliente inactivo específico dentro del filtro de período que hayas aplicado.
              </li>
            </ol>
            <p className="mt-2 text-content-2">Los resultados te mostrarán detalles como el nombre del cliente, sus datos de contacto, los días exactos que lleva inactivo y la fecha de su última visita.</p>
          </div>
        )
      },
      {
        id: 'inactive-actions',
        question: '¿Qué acciones puedo tomar con la lista de clientes inactivos?',
        answer: (
          <div>
            <p>Una vez que has identificado a tus clientes inactivos (función PRO), puedes emprender varias acciones para intentar recuperarlos:</p>
            <ul className="list-disc ml-5 mt-2 space-y-2">
              <li>
                <strong>Análisis y Contacto Individual:</strong> Haz clic en el nombre de un cliente para ver su <button onClick={() => navigateCallback('/dashboard/clients')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">historial completo</button>. Esto te dará contexto para un mensaje personal.
              </li>
              <li>
                <strong>Campañas de Reactivación (PRO):</strong> Usa la función de <button onClick={() => navigateCallback('/dashboard/campaigns')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium inline-flex items-center"><MessageSquare className="h-4 w-4 mr-1" />Campañas</button> para diseñar y enviar mensajes de WhatsApp específicamente a este segmento de clientes inactivos, ofreciéndoles una promoción de regreso, novedades, etc.
              </li>
              <li>
                <strong>Incentivos con Puntos (PRO):</strong> Considera otorgar puntos de bonificación a través de la sección <button onClick={() => navigateCallback('/dashboard/recharge-points')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium inline-flex items-center"><Clock className="h-4 w-4 mr-1" />Gestionar Puntos</button> como un "regalo de bienvenida" para motivar su retorno.
              </li>
              <li>
                <strong>Exportar Datos (PRO):</strong> Puedes exportar la lista filtrada de clientes inactivos a Excel para un análisis más profundo o para usarla en otras herramientas de marketing externas (ej. para campañas de publicidad lookalike o retargeting).
              </li>
            </ul>
            <div className="mt-3 bg-blue-50 dark:bg-blue-500/10 p-3 rounded-md border border-blue-200 dark:border-blue-500/30">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Nota:</strong> Las campañas de WhatsApp se envían de forma <strong>espaciada y cuidadosa</strong> a lo largo de horas o días (nunca todas de golpe) para proteger tu número de WhatsApp de bloqueos.
              </p>
            </div>
            <p className="mt-3 text-content-2">Recuerda que todas estas acciones avanzadas forman parte de los beneficios de la suscripción PRO.</p>
          </div>
        )
      },
      {
        id: 'inactive-best-practices',
        question: '¿Cuáles son las mejores prácticas para recuperar clientes inactivos?',
        answer: (
          <div>
            <p>Implementar estrategias efectivas para recuperar clientes inactivos (función PRO) puede tener un gran impacto. Considera estos consejos:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-2">
              <li>
                <strong>Segmenta tu Audiencia:</strong> No todos los clientes inactivos son iguales. Un cliente que no visita en 30 días puede necesitar un mensaje diferente a uno que lleva 6 meses inactivo. Adapta tu comunicación.
              </li>
              <li>
                <strong>Personaliza el Mensaje:</strong> Dirígete al cliente por su nombre. Si es posible, referencia su última visita o compra. Un mensaje como "¡Te extrañamos, [Nombre]!" es más efectivo que uno genérico.
              </li>
              <li>
                <strong>Ofrece un Incentivo Claro y Valioso:</strong> Un descuento especial, puntos extra en su próxima visita, un producto de regalo, o acceso a una novedad pueden ser el empujón que necesitan.
              </li>
              <li>
                <strong>Crea un Sentido de Urgencia (Opcional):</strong> Ofertas por tiempo limitado pueden motivar una acción más rápida ("Vuelve esta semana y obtén X").
              </li>
              <li>
                <strong>Facilita el Regreso:</strong> Asegúrate de que el proceso para redimir la oferta o volver a interactuar sea simple.
              </li>
              <li>
                <strong>Mide y Ajusta:</strong> Realiza un seguimiento de qué campañas de reactivación funcionan mejor y optimiza tus estrategias basándote en los resultados.
              </li>
            </ol>
            <div className="mt-3 bg-blue-50 dark:bg-blue-500/10 p-3 rounded-md border border-blue-200 dark:border-blue-500/30">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Dato interesante:</strong> Según diversos estudios de marketing, adquirir un nuevo cliente puede costar entre 5 y 25 veces más que retener a uno existente. Por ello, invertir en estrategias para recuperar clientes inactivos suele ser muy rentable.
              </p>
            </div>
          </div>
        )
      },
    ]
  };
};

// export default InactiveClientsFAQs; // Opcional